/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type RenameModalProps = BasicModalProps<RenameModalDto> & {
    /**
     * The initial name of the resource to rename.
     */
    name: string;

    /**
     * The label of the name to display.
     */
    nameLabel?: string;

    /**
     * The placeholder of the description field to display.
     */
    descriptionPlaceholder?: string;

    /**
     * Indicator whether to have description field.
     */
    withDescription?: boolean;

    /**
     * The initial description of the resource to rename.
     */
    description?: string;

    /**
     * Names to be excluded in order to have unique names.
     */
    existingEntityNames?: string[];

    /**
     * max length for the new name's characters.
     */
    nameMaxLength?: number;
};

export type RenameModalDto = {
    /**
     * The name after it has been mutated.
     */
    name: string;

    /**
     * The description after it has been mutated.
     */
    description?: string;
};

/**
 * Component strings and their localization file ids.
 */
const messages = defineMessages({
    defaultTitle: {
        id: 'core.renameModal.defaultTitle',
        defaultMessage: 'Done'
    },
    nameLabel: {
        id: 'core.renameModal.nameLabel',
        defaultMessage: 'Name'
    },
    descriptionLabel: {
        id: 'core.renameModal.descriptionLabel',
        defaultMessage: 'Description'
    },
    namePlaceholder: {
        id: 'core.renameModal.namePlaceholder',
        defaultMessage: 'Type in name ...'
    },
    duplicatedNameErrorMessage: {
        id: 'core.renameModal.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    }
});

/**
 * Represents a modal to be used for all rename operations on
 * LUIS resources.
 *
 * @param props The modal properties.
 */
export const RenameModal = withModalMounter((props: RenameModalProps) => {
    const { localize } = useLocalization();
    const {
        name,
        onSubmit,
        description,
        existingEntityNames,
        dialogContentProps = {},
        withDescription = false,
        descriptionPlaceholder = '',
        nameLabel = localize(messages.nameLabel),
        nameMaxLength
    } = props;

    const title = dialogContentProps.title ?? localize(messages.defaultTitle);

    const { formState, formStateDto, updateFormState } = useFormState<RenameModalDto>({
        name: [
            name,
            FormValidators.createRequiredValidator(),
            FormValidators.createBlacklistValidator(),
            nameMaxLength ? FormValidators.createLengthValidator(nameMaxLength) : _ => undefined,
            FormValidators.createDuplicateValueValidator(name)
        ],
        description: [description || '']
    });

    const duplicateErrorMessages =
        existingEntityNames?.find(n => n === formState.fields.name.value) && localize(messages.duplicatedNameErrorMessage);

    return (
        <BasicModal
            {...props}
            onSubmit={() => onSubmit(formStateDto)}
            dialogContentProps={{ title, ...dialogContentProps }}
            valid={formState.form.valid && !duplicateErrorMessages}>
            <Stack tokens={{ childrenGap: 20 }}>
                <LuisTextField
                    required
                    autoFocus
                    label={nameLabel}
                    data-automation-id="nameInput"
                    value={formState.fields.name.value}
                    placeholder={localize(messages.namePlaceholder)}
                    onChange={(_, value) => updateFormState({ name: value })}
                    onEnterKeyUp={() => formState.form.valid && onSubmit(formStateDto)}
                    errorMessage={formState.fields.name.errorMessage || duplicateErrorMessages}
                />

                {withDescription && (
                    <LuisTextField
                        multiline
                        placeholder={descriptionPlaceholder}
                        data-automation-id="descriptionInput"
                        value={formState.fields.description.value}
                        label={localize(messages.descriptionLabel)}
                        onChange={(_, value) => updateFormState({ description: value })}
                        onEnterKeyUp={() => formState.form.valid && onSubmit(formStateDto)}
                    />
                )}
            </Stack>
        </BasicModal>
    );
});
